import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface QueueFiltersProps {
  onStatusFilter: (status: string | null) => void;
  onSearchFilter: (search: string) => void;
  activeStatus: string | null;
  searchTerm: string;
}

export function QueueFilters({ 
  onStatusFilter, 
  onSearchFilter, 
  activeStatus, 
  searchTerm,
  statusCounts = {}
}: QueueFiltersProps & { statusCounts?: Record<string, number> }) {
  const [localSearch, setLocalSearch] = useState(searchTerm);
  const { t } = useTranslation('common');

  const statusOptions = [
    { key: null, label: t('common.all'), count: 0 },
    { key: 'queued', label: t('queue.status.queued'), count: 0 },
    { key: 'running', label: t('queue.status.running'), count: 0 },
    { key: 'completed', label: t('queue.completed'), count: 0 },
    { key: 'failed', label: t('queue.failed'), count: 0 }
  ];

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearchFilter(localSearch);
  };

  const clearSearch = () => {
    setLocalSearch('');
    onSearchFilter('');
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <form onSubmit={handleSearchSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('common.search') + '...'}
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="pl-9"
          />
          {localSearch && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0"
              onClick={clearSearch}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Button type="submit" variant="outline">
          {t('common.search')}
        </Button>
      </form>

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2">
        {statusOptions.map(option => {
          const count = option.key ? (statusCounts[option.key] || 0) : Object.values(statusCounts).reduce((a, b) => a + b, 0);
          const isActive = activeStatus === option.key;
          
          return (
            <Button
              key={option.key || 'all'}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => onStatusFilter(option.key)}
              className="gap-2"
            >
              {option.label}
              <Badge 
                variant={isActive ? "secondary" : "outline"}
                className="h-5 text-xs"
              >
                {count}
              </Badge>
            </Button>
          );
        })}
      </div>
    </div>
  );
}